import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Divider, Tooltip } from "@mui/material";
import { WrapWordContent, WrapWord, WrapWordText, Word, WrapAddWord, PointTimeStyles } from './Style'; // ודאי נתיבים בהתאם למיקום הקובץ
import { InsertionIcon, WordSettings, wordHooks } from ".";
import { IconButton } from "components/Form";
import { errorHooks } from "./ErrorTypes";
import { incorrect } from "keys/types";
import { updateType, vowelSound } from "keys";
import { viewResultsSettings } from './hooks/constants';
import { Img } from "styles/GlobalStyle";
import { WordsContext } from "./Words";
import { secretStorage } from "config";

/**
 * קומפוננטת WordsPage
 * אחראית על רינדור של מערך מילים בתוך עמוד ספציפי.
 * מטפלת בתצוגת המילה (טקסט / תעתוק / תמונה), סימוני שגיאות, זמנים (PointTime), וכלים לעריכה.
 * * @param {Array} words - מערך המילים שיוצגו בעמוד זה
 * @param {boolean} viewResults - מצב צפייה בתוצאות (קריאה בלבד)
 * @param {boolean} markOption - מצב עריכה/סימון פתוח
 * @param {number} prevWordIndex - האינדקס המצטבר של המילים מהעמודים הקודמים (לצורך חישוב אינדקס גלובלי)
 */
const WordsPage = (props) => {
    const { words, viewResults, markOption, prevWordIndex } = props;

    // קריאת נתונים מה-Context וה-Redux
    const context = useContext(WordsContext);
    const { wordAction } = context || {};
    const { assessment } = useSelector(state => state.assessment);
    const { endPoint = {}, transcriptionStopPoint = {}, timePoint = {} } = assessment || {};

    // הרשאות וכלים
    const isSuperuser = secretStorage.getItem("user")?.is_superuser;
    const wordInsertion = wordHooks.useInsertion();
    const getTooltipTitle = errorHooks.useGetTooltipTitle();

    return (
        words?.map((word, index) => {
            const { removed, minorError, word_def_text, customStatus, transcription, status, word_def_fe_details } = word;
            const { new_page, break_line } = word_def_fe_details || {};

            // הגדרת משתני התצוגה והשגיאות למילה הנוכחית
            const settingsValues = {
                error: status === incorrect && !minorError,
                minorError: minorError,
                timePoint: timePoint?.word_def_id === word.word_def_id ? timePoint.value : "",
                end: endPoint.word_def_id === word.word_def_id,
                endTranscription: transcriptionStopPoint.word_def_id === word.word_def_id,
                customStatus: customStatus,
                removed: removed
            };

            const { error: errorVal, minorError: minorErrorVal, timePoint: timePointVal, end: endVal, customStatus: customStatusVal } = settingsValues;
            const nextWord = words?.[index + 1 < words?.length ? index + 1 : index];
            let tooltipContent = getTooltipTitle(errorVal, minorError, removed, customStatus);

            // בדיקה האם להציג את המסגרת של הבינה המלאכותית
            const showAIHint = word.ai_review_status === 'REVIEW' 
            // && word.status === 'UNCHECKED';

            // הערה מקורית שלך: if (!word.word_def_id) renderData = false

            return (
                <React.Fragment key={index}>
                    {/* 1. מפריד במקרה של עמוד חדש */}
                    {new_page &&
                        <div className="new-page">
                            <Divider />
                        </div>
                    }

                    {/* 2. רינדור המילה עצמה (אם קיים לה ID) */}
                    {word.word_def_id ? (
                        <WrapWordContent isSuperuser={isSuperuser} onClick={() => wordAction?.(word, index)}>
                            <div className="word-item">

                                {/* תפריט הגדרות מילה במצב עריכה */}
                                {markOption && (
                                    <WordSettings
                                        index={prevWordIndex + index}
                                        word={word}
                                        settingsValues={settingsValues}
                                    />
                                )}

                                {/* תפריט הגדרות מילה במצב צפייה */}
                                {viewResults && (
                                    <WordSettings
                                        index={prevWordIndex + index}
                                        word={word}
                                        settings={viewResultsSettings}
                                    />
                                )}

                                {/* התוכן הוויזואלי של המילה */}
                                <WrapWord>
                                    <WrapWordText>
                                        <Tooltip title={tooltipContent} placement="top">
                                            <Word
                                                $removed={removed}
                                                $customStatus={customStatusVal}
                                                $minorError={minorErrorVal}
                                                $error={errorVal}
                                                $showAIHint={showAIHint}
                                            >

                                                {/* בדיקה אם קיימת תמונה (ניקוד/צליל) או שמוצג טקסט/תעתוק */}
                                                {vowelSound[word_def_text] ? (
                                                    <div className={markOption ? 'transcription-word-image' : 'word-image'}>
                                                        <Img src={vowelSound[word_def_text].image} />
                                                    </div>
                                                ) : (
                                                    transcription || word_def_text
                                                )}

                                                {/* תצוגת בועת הזמן (נקודת זמן) אם קיימת */}
                                                {timePointVal && <PointTimeStyles>{timePointVal}</PointTimeStyles>}
                                            </Word>
                                        </Tooltip>
                                    </WrapWordText>
                                </WrapWord>
                            </div>

                            {/* כפתור הוספת מילה (אייקון פלוס/אזהרה) בין המילים */}
                            {markOption && (
                                <WrapAddWord>
                                    <IconButton
                                        $bg
                                        {...wordInsertion}
                                        color={
                                            (nextWord.word_def_id || nextWord.update_type === updateType.delete)
                                                ? "transparent"
                                                : (nextWord.minorError ? "rating4" : "error")
                                        }
                                        action={() => wordInsertion.action(word, prevWordIndex + index)}
                                    />
                                </WrapAddWord>
                            )}
                        </WrapWordContent>
                    ) : (
                        /* 3. במקרה שאין ID למילה והיא לא במצב סימון, נציג אייקון הכנסה */
                        !markOption && (
                            <div className="insertion-icon">
                                <InsertionIcon />
                            </div>
                        )
                    )}

                    {/* 4. שבירת שורה אם הוגדרה */}
                    {break_line && !new_page && <div style={{ width: "100%" }} />}
                </React.Fragment>
            );
        })
    );
};

export default WordsPage;